import { useState, useEffect } from "react";
import {
  formatLicenseNumber,
  formatState,
  formatTerminationDate,
} from "../../../js/formatting";
import {
  agentLicensesByState,
  filteredLicenses,
  generateLicensedStates,
  generateLicenseStatuses,
  generateTerminationDateStatusObject,
  isLicenseExpired,
  isLicenseAlmostExpired,
} from "./utilities/licenseUtils";
import { useRowPagination } from "../../../hooks/useRowPagination";
import { useTranslation } from "react-i18next";

import { AgentLicenseCard } from "./AgentLicenseCard";
import { AgentLicenseFilterForm } from "./AgentLicenseFilterForm";
import RowPagination from "../../General/RowPagination";

const AgentLicensesContainer = (props) => {
  // sort agentLicenses by state
  const [agentLicenses] = useState(agentLicensesByState(props.agentLicenses));
  const { t } = useTranslation();

  // configuration settings for this container
  const agentLicenseConfigurations = {
    isEnabled: true,
    isLicenseNumberRendered: false,
    status: {
      expired: {
        status: "problem",
        message: "License expired",
      },
      almostExpired: {
        status: "risk",
        message: "Time to renew",
      },
      default: {
        status: "good",
        message: "Active",
      },
      nA: {
        status: "good",
        message: "N/A",
      },
      warnForTimeQuantity: 30,
      warnForTimeUnit: "days",
    },
  };

  const initialFilterState = {
    state: "all",
    status: "all",
  };

  // state for filter form
  const [filters, setFilters] = useState(initialFilterState);

  const filterHandler = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const resetForm = () => {
    setFilters(initialFilterState);
    return false;
  };

  //state for pagination
  const [filtered, setFiltered] = useState(
    filteredLicenses(
      agentLicensesByState(agentLicenses),
      filters,
      initialFilterState,
      agentLicenseConfigurations.status
    )
  );

  const { rowsForDisplay: rows, rowPaginationProps } = useRowPagination(
    filtered,
    [12, 24, 36, "all"]
  );

  useEffect(() => {
    setFiltered(
      filteredLicenses(
        agentLicensesByState(agentLicenses),
        filters,
        initialFilterState,
        agentLicenseConfigurations.status
      )
    );
    // eslint-disable-next-line
  }, [filters]);

  return (
    <>
      {agentLicenseConfigurations.isEnabled ? (
        <section className="container ui-component-spacing">
          <h2>{t("AgentProfile-title3")}</h2>
          <p className="text-sm">{t("AgentProfile-subTitle3")}</p>
          <hr />
          <div>
            <AgentLicenseFilterForm
              stateList={generateLicensedStates(agentLicenses)}
              statusList={generateLicenseStatuses(
                agentLicenses,
                agentLicenseConfigurations.status
              )}
              filterHandler={filterHandler}
              resetForm={resetForm}
            />
          </div>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {rows?.map(({ occurance, number, state, terminationDate }, idx) => {
              return (
                <AgentLicenseCard
                  isLicenseExpired={isLicenseExpired(terminationDate)}
                  isLicenseAlmostExpired={isLicenseAlmostExpired(
                    terminationDate,
                    agentLicenseConfigurations.status
                  )}
                  key={`$Agent-License-${occurance}-${idx}`}
                  licenseNumber={
                    agentLicenseConfigurations.isLicenseNumberRendered
                      ? formatLicenseNumber(number)
                      : false
                  }
                  licenseState={formatState(state)}
                  statusObject={generateTerminationDateStatusObject(
                    terminationDate,
                    agentLicenseConfigurations.status
                  )}
                  terminationDate={formatTerminationDate(terminationDate)}
                />
              );
            })}
          </div>
          <RowPagination {...rowPaginationProps} />
        </section>
      ) : (
        <></>
      )}
    </>
  );
};

export { AgentLicensesContainer };
